import React, { useEffect, useRef, useState } from "react";
import ErrorMessage from "../../../../Components/Common/ErrorMessage/ErrorMessage";
import Button from "../../../../Components/Common/Button/Button";
import Skeleton from "../../../../Components/Common/Skeleton/Skeleton";
import Input from "../../../../Components/Common/Input/Input";
import SearchableSelect from "Components/Common/SearchableSelect/SearchableSelect";
import SectionCard from "./SectionCard";
import PrevIcon from "Assets/Img/PrevIcon";
import NextIcon from "Assets/Img/NextIcon";
import StepTick from "Assets/Img/StepTick";
import useDebounce from "hooks/useDebounce";
import { getActiveSovLienItems, getSubcontractors } from "services";
import { uploadFileToS3 } from "../../GcPreconCommercial/UploadFileToS3";
import { useToast } from "../../../../Components/Common/Toast/ToastContext";
import type { DrawLineItem, SovItemPayload } from "../pages/DrawRequestPage";

interface SovOption {
  id: string;
  aiaItemCode: number;
  description: string;
  subContractor: { id: string; companyName: string } | null;
  amount: { amount: number } | null;
  aiaItem: { id: string };
}

interface VendorOption {
  id: string;
  name: string;
}

interface LineItemsStepProps {
  projectId: string;
  lineItems: DrawLineItem[];
  onAddLine: () => void;
  onDeleteLine: (id: number) => void;
  onUpdateLine: (id: number, key: keyof DrawLineItem, value: string) => void;
  onSelectSovItem: (rowId: number, sov: SovItemPayload) => void;
  onClearSovItem: (rowId: number) => void;
  onSelectVendor: (rowId: number, vendorId: string, vendorName: string) => void;
  onAddVendor: (rowId: number) => void;
  onAttachInvoice: (rowId: number, key: string, name: string) => void;
  onRemoveInvoice: (rowId: number) => void;
  onToggleOffsite: (id: number) => void;
  onUpdateOffsiteAmount: (id: number, value: string) => void;
  onBack: () => void;
  onNext: () => void;
  nextLoading?: boolean;
  totalAmount: string;
  loading?: boolean;
  submitError?: string;
  bankCompanyName: string;
}

const PAGE_SIZE = 10;
const DROPDOWN_MENU_WIDTH = 320;

const parseCurrency = (value: string): number => {
  const amount = Number(value.replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const formatCurrency = (value: number): string =>
  value.toLocaleString("en-US", { style: "currency", currency: "USD" });

const LineItemsStep = ({
  projectId,
  lineItems,
  onAddLine,
  onDeleteLine,
  onUpdateLine,
  onSelectSovItem,
  onClearSovItem,
  onSelectVendor,
  onAddVendor,
  onAttachInvoice,
  onRemoveInvoice,
  onToggleOffsite,
  onUpdateOffsiteAmount,
  onBack,
  onNext,
  nextLoading = false,
  totalAmount,
  loading = false,
  submitError = "",
  bankCompanyName,
}: LineItemsStepProps) => {
  const showToast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingRowId = useRef<number | null>(null);
  const submitErrorRef = useRef<HTMLDivElement>(null);
  const [uploadingIds, setUploadingIds] = useState<Set<number>>(new Set());

  type RowErrors = {
    sovItemId?: boolean;
    subContractorId?: boolean;
    amount?: boolean;
  };
  const [rowErrors, setRowErrors] = useState<Record<number, RowErrors>>({});
  const [noItemsError, setNoItemsError] = useState(false);

  useEffect(() => {
    if (lineItems.length > 0) setNoItemsError(false);
  }, [lineItems.length]);
  useEffect(() => {
    if (submitError && submitErrorRef.current) {
      submitErrorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [submitError]);

  const handleNext = () => {
    if (lineItems.length === 0) {
      setNoItemsError(true);
      return;
    }
    setNoItemsError(false);
    const newRowErrors: Record<number, RowErrors> = {};
    lineItems.forEach((item) => {
      const err: RowErrors = {};
      if (!item.sovItemId) err.sovItemId = true;
      if (!item.subContractorId) err.subContractorId = true;
      if (!item.amount || parseCurrency(item.amount) === 0) err.amount = true;
      if (Object.keys(err).length > 0) newRowErrors[item.id] = err;
    });
    if (Object.keys(newRowErrors).length > 0) {
      setRowErrors(newRowErrors);
      setTimeout(() => {
        const el = document.querySelector<HTMLElement>(
          ".input-error .ss-input, .error-input-red",
        );
        el?.closest("td")?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        el?.focus();
      }, 50);
      return;
    }
    setRowErrors({});
    onNext();
  };
  const handleInvoiceClick = (rowId: number) => {
    pendingRowId.current = rowId;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const rowId = pendingRowId.current;
    e.target.value = "";
    if (!file || rowId === null) return;
    setUploadingIds((prev) => new Set([...prev, rowId]));
    try {
      const result: any = await uploadFileToS3(file);
      onAttachInvoice(rowId, result.key, file.name);
      showToast("Invoice attached successfully");
    } catch (err: any) {
      showToast(err.message || "Failed to upload invoice");
    } finally {
      setUploadingIds((prev) => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
    }
  };

  const missingInvoices = lineItems.filter((item) => !item.invoiceAttached);
  const offsiteTotal = lineItems
    .filter((item) => item.offsiteStored)
    .reduce((sum, item) => sum + parseCurrency(item.offsiteAmount), 0);

  // ── SOV items ──────────────────────────────────────────────────────────────
  const [sovOptions, setSovOptions] = useState<SovOption[]>([]);
  const [sovLoading, setSovLoading] = useState(false);
  const [sovHasMore, setSovHasMore] = useState(false);
  const [sovRawSearch, setSovRawSearch] = useState("");
  const [sovLoadParams, setSovLoadParams] = useState({ search: "", page: 1 });
  const sovHasFetched = useRef(false);

  const debouncedSovSearch = useDebounce(sovRawSearch, 700);

  useEffect(() => {
    if (!sovHasFetched.current) return;
    setSovLoadParams({ search: debouncedSovSearch.trim(), page: 1 });
  }, [debouncedSovSearch]);

  useEffect(() => {
    if (!sovHasFetched.current || !projectId) return;
    let cancelled = false;
    const load = async () => {
      setSovLoading(true);
      const { data, error } = (await getActiveSovLienItems(
        projectId,
        sovLoadParams.page,
        PAGE_SIZE,
        sovLoadParams.search,
      )) as { data: any; error: any };
      if (cancelled) return;
      if (!error) {
        const list: SovOption[] = Array.isArray(data?.data?.data)
          ? data.data.data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        const pagination = data?.data?.pagination;
        setSovOptions((prev) =>
          sovLoadParams.page === 1 ? list : [...prev, ...list],
        );
        setSovHasMore(
          pagination ? pagination.page < pagination.totalPages : false,
        );
      }
      setSovLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, sovLoadParams]);

  const handleSovOpen = () => {
    if (sovHasFetched.current) return;
    sovHasFetched.current = true;
    setSovLoadParams({ search: "", page: 1 });
  };

  const handleSovSearch = (q: string) => setSovRawSearch(q.trim());

  const handleSovLoadMore = () => {
    if (!sovLoading && sovHasMore)
      setSovLoadParams((p) => ({ ...p, page: p.page + 1 }));
  };

  // ── Vendor (subcontractors) ────────────────────────────────────────────────
  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendorHasMore, setVendorHasMore] = useState(false);
  const [vendorRawSearch, setVendorRawSearch] = useState("");
  const [vendorLoadParams, setVendorLoadParams] = useState({
    search: "",
    page: 1,
  });
  const vendorHasFetched = useRef(false);

  const debouncedVendorSearch = useDebounce(vendorRawSearch, 700);

  useEffect(() => {
    if (!vendorHasFetched.current) return;
    setVendorLoadParams({ search: debouncedVendorSearch.trim(), page: 1 });
  }, [debouncedVendorSearch]);

  useEffect(() => {
    if (!vendorHasFetched.current) return;
    let cancelled = false;
    const load = async () => {
      setVendorLoading(true);
      const { data, error } = (await getSubcontractors(
        vendorLoadParams.search,
        vendorLoadParams.page,
        PAGE_SIZE,
      )) as { data: any; error: any };
      if (cancelled) return;
      if (!error) {
        const list: VendorOption[] = (
          Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
              ? data
              : []
        )
          .map((s: any) => ({ id: s.id, name: s.companyName ?? s.name ?? "" }))
          .filter((s: VendorOption) => s.id && s.name);
        const pagination = data?.pagination;
        setVendorOptions((prev) =>
          vendorLoadParams.page === 1 ? list : [...prev, ...list],
        );
        setVendorHasMore(
          list.length > 0 && pagination
            ? pagination.page < pagination.totalPages
            : false,
        );
      }
      setVendorLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [vendorLoadParams]);

  const handleVendorOpen = () => {
    if (vendorHasFetched.current) return;
    vendorHasFetched.current = true;
    setVendorLoadParams({ search: "", page: 1 });
  };

  const handleVendorSearch = (q: string) => setVendorRawSearch(q.trim());

  const handleVendorLoadMore = () => {
    if (!vendorLoading && vendorHasMore)
      setVendorLoadParams((p) => ({ ...p, page: p.page + 1 }));
  };

  if (loading) {
    return (
      <section className="screen activeScreen">
        <Skeleton width="35%" height={22} style={{ marginBottom: 8 }} />
        <Skeleton width="65%" height={14} style={{ marginBottom: 24 }} />
        <div
          style={{
            border: "1px solid var(--bd)",
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--bd)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Skeleton width={140} height={14} />
            <Skeleton width={110} height={32} borderRadius={7} />
          </div>
          <div style={{ padding: 16 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1.5fr 1fr 1fr 80px 100px 32px",
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                {Array.from({ length: 7 }).map((_, j) => (
                  <Skeleton key={j} height={34} borderRadius={6} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="screen activeScreen" aria-labelledby="line-items-title">
      <h2 className="sectionTitle" id="line-items-title">
        Line Items &amp; Invoices
      </h2>
      <p className="sectionSub">
        Each line item must match an SOV line. Attach an invoice for every item
        being billed. If any materials for this line item are stored off-site,
        check the box to enter the stored amount — this flows into Column F of
        the AIA G703.
      </p>
      {submitError && (
        <div ref={submitErrorRef} className="warningBox errorBox mb-4">
          {submitError}
        </div>
      )}

      {noItemsError && (
        <div className="warningBox errorBox">
          Please add at least one line item before proceeding.
        </div>
      )}

      <SectionCard
        title="Check Disbursement List"
        action={
          <Button className="btn btnPrimary btnSmall" onClick={onAddLine}>
            + Add Line Item
          </Button>
        }
      >
        <div className="tableWrap">
          <table className="drawTable">
            <thead>
              <tr>
                <th className="thSmall">AIA Item #</th>
                <th>Vendor / Payee</th>
                <th className="thSmall">This Period</th>
                <th>Notes</th>
                <th className="osCheckCell">Off-Site Stored?</th>
                <th className="thSmall">Invoice</th>
                <th className="thTiny" aria-label="Delete" />
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item) => (
                <React.Fragment key={item.id}>
                  <tr>
                    <td>
                      <SearchableSelect<SovOption>
                        portal
                        menuWidth={DROPDOWN_MENU_WIDTH}
                        className={`aiaItemSelect ${rowErrors[item.id]?.sovItemId ? " input-error" : ""}`}
                        value={item.sovItemId}
                        valueLabel={
                          item.aiaItemCode ? String(item.aiaItemCode) : ""
                        }
                        options={sovOptions}
                        isOptionDisabled={(o) => {
                          return lineItems.some(
                            (row) => row.aiaItemId === o?.aiaItem.id,
                          );
                        }}
                        apiSearch
                        onSearch={handleSovSearch}
                        onOpen={handleSovOpen}
                        onLoadMore={handleSovLoadMore}
                        hasMore={sovHasMore && !sovLoading}
                        loading={sovLoading}
                        getOptionValue={(o) => o.id}
                        getOptionLabel={(o) =>
                          `${o.aiaItemCode} - ${o.description}`
                        }
                        getSelectedLabel={(o) => String(o.aiaItemCode)}
                        placeholder="Select AIA"
                        onChange={(_, option) => {
                          if (option) {
                            onSelectSovItem(
                              item.id,
                              option as unknown as SovItemPayload,
                            );
                            setRowErrors((prev) => ({
                              ...prev,
                              [item.id]: {
                                ...prev[item.id],
                                sovItemId: false,
                                ...(option.subContractor
                                  ? { subContractorId: false }
                                  : {}),
                              },
                            }));
                          } else {
                            onClearSovItem(item.id);
                            setRowErrors((prev) => {
                              if (!prev[item.id]) return prev;
                              return {
                                ...prev,
                                [item.id]: {
                                  ...prev[item.id],
                                  sovItemId: true,
                                  subContractorId: true,
                                },
                              };
                            });
                          }
                        }}
                      />
                      <ErrorMessage
                        touched={!!rowErrors[item.id]?.sovItemId}
                        error="Required"
                      />
                    </td>
                    <td>
                      <SearchableSelect<VendorOption>
                        portal
                        menuWidth={DROPDOWN_MENU_WIDTH}
                        className={`aiaItemSelect venderSelect ${
                          rowErrors[item.id]?.subContractorId
                            ? "input-error"
                            : ""
                        }`}
                        value={item.subContractorId}
                        valueLabel={item.vendor}
                        options={vendorOptions}
                        onCreateNew={() => onAddVendor(item.id)}
                        createNewLabel="+ Add new vendor"
                        apiSearch
                        onSearch={handleVendorSearch}
                        onOpen={handleVendorOpen}
                        onLoadMore={handleVendorLoadMore}
                        hasMore={vendorHasMore && !vendorLoading}
                        loading={vendorLoading}
                        getOptionValue={(o) => o.id}
                        getOptionLabel={(o) => o.name}
                        placeholder="Select vendor"
                        onChange={(vendorId, option) => {
                          onSelectVendor(item.id, vendorId, option?.name ?? "");
                          if (vendorId)
                            setRowErrors((prev) => ({
                              ...prev,
                              [item.id]: {
                                ...prev[item.id],
                                subContractorId: false,
                              },
                            }));
                        }}
                      />
                      <ErrorMessage
                        touched={!!rowErrors[item.id]?.subContractorId}
                        error="Required"
                      />
                    </td>
                    <td>
                      <div className="prefixInputWrap">
                        <span className="prefixSymbol">$</span>
                        <Input
                          type="number"
                          value={item.amount}
                          className={
                            (rowErrors[item.id]?.amount && !item.amount) ||
                            (item.sovAmount !== null &&
                              parseCurrency(item.amount) > item.sovAmount)
                              ? "error-input-red"
                              : ""
                          }
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            onUpdateLine(item.id, "amount", e.target.value);
                            if (
                              e.target.value &&
                              parseCurrency(e.target.value) > 0
                            )
                              setRowErrors((prev) => ({
                                ...prev,
                                [item.id]: { ...prev[item.id], amount: false },
                              }));
                          }}
                          placeholder="0.00"
                        />
                      </div>
                      <ErrorMessage
                        touched={!!(rowErrors[item.id]?.amount && !item.amount)}
                        error="Required"
                      />
                      {item.sovAmount !== null &&
                        parseCurrency(item.amount) > item.sovAmount && (
                          <ErrorMessage
                            touched
                            error={`Max ${formatCurrency(item.sovAmount)}`}
                          />
                        )}
                    </td>
                    <td>
                      <Input
                        className=""
                        value={item.notes}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onUpdateLine(item.id, "notes", e.target.value)
                        }
                        placeholder="Notes"
                      />
                    </td>
                    <td className="osCheckCell">
                      <label
                        className="osCheckWrap"
                        title="Materials stored off-site"
                      >
                        <Input
                          type="checkbox"
                          checked={item.offsiteStored}
                          onChange={() => onToggleOffsite(item.id)}
                        />
                        {/* <span className="osCheckLabel">Col F</span> */}
                      </label>
                    </td>
                    <td>
                      {item.invoiceAttached ? (
                        <span className="invAttachedWrap">
                          <span
                            className="invBtn attached"
                            title={item.invoiceName || "Invoice"}
                          >
                            <StepTick />
                            <span className="invBtnLabel">
                              {item.invoiceName || "Invoice"}
                            </span>
                            <span
                              className="invRemoveBtn"
                              onClick={() => onRemoveInvoice(item.id)}
                            >
                              ×
                            </span>
                          </span>
                        </span>
                      ) : (
                        <Button
                          className="invBtn"
                          onClick={() => handleInvoiceClick(item.id)}
                          disabled={uploadingIds.has(item.id)}
                          type="button"
                        >
                          {uploadingIds.has(item.id) ? (
                            <>
                              <div className="btn-spinner" />
                              Uploading
                            </>
                          ) : (
                            "Attach Invoice"
                          )}
                        </Button>
                      )}
                    </td>
                    <td>
                      <Button
                        className="delBtn"
                        onClick={() => onDeleteLine(item.id)}
                        type="button"
                        aria-label={`Delete line ${item.id}`}
                      >
                        X
                      </Button>
                    </td>
                  </tr>
                  <tr
                    className={`offsiteRow ${item.offsiteStored ? "open" : ""}`.trim()}
                  >
                    <td colSpan={7}>
                      <div className="offsiteInner">
                        <div>
                          <span className="offsiteLabel">
                            Off-Site Stored:{" "}
                          </span>
                          <span className="offsiteNote">
                            {item.offsiteNote}
                          </span>
                        </div>
                        <div className="d-flex mt-3 align-items-center gap-1">
                          <span className="offsiteDollar">Amount: $</span>
                          <Input
                            className={`offsiteInput${parseCurrency(item.offsiteAmount) > parseCurrency(item.amount) ? " error-input-red" : ""}`}
                            type="text"
                            value={item.offsiteAmount}
                            placeholder="0.00"
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => onUpdateOffsiteAmount(item.id, e.target.value)}
                          />
                        </div>
                        {parseCurrency(item.offsiteAmount) >
                          parseCurrency(item.amount) && (
                          <ErrorMessage
                            touched
                            error={`Max ${formatCurrency(parseCurrency(item.amount))}`}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="totalRow">
                <td colSpan={2} className="totalLabel">
                  Work completed this period
                </td>
                <td>{totalAmount}</td>
                <td colSpan={4} />
              </tr>
              {offsiteTotal > 0 && (
                <tr className="totalRow offsiteTotalRow">
                  <td colSpan={2} className="totalLabel">
                    Materials stored off-site
                  </td>
                  <td>{formatCurrency(offsiteTotal)}</td>
                  <td colSpan={4} />
                </tr>
              )}
            </tfoot>
          </table>
        </div>

        {missingInvoices.length > 0 && (
          <div className="warningBox">
            <strong>
              {missingInvoices.length} invoice
              {missingInvoices.length > 1 ? "s" : ""} missing.
            </strong>{" "}
            {(() => {
              const names = [
                ...new Set(
                  missingInvoices.map((i) => i.vendor).filter(Boolean),
                ),
              ];
              if (names.length === 0) return "Some vendors";
              if (names.length === 1) return names[0];
              if (names.length === 2) return `${names[0]} and ${names[1]}`;
              return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
            })()}{" "}
            still need invoices attached before this draw can be submitted.
          </div>
        )}

        <div className="disclaimerBox offsiteDisclaimer">
          <strong>Off-Site Stored Materials :</strong> Check the box on any line
          item where materials have been purchased and paid for but are not yet
          incorporated into the work on site. A Right of Entry letter from the
          vendor, the storage location, a copy of the invoice, and certificate
          of insurance are required by {bankCompanyName} for any off-site stored
          materials billing. These amounts appear in Column F of the AIA G703
          and are included in the total completed and stored value.
        </div>
      </SectionCard>

      <Input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      <div className="btnRow spaced">
        <Button className="btn btnSecondary" onClick={onBack}>
          <PrevIcon /> Back
        </Button>
        <Button
          className="btn btnPrimary"
          onClick={handleNext}
          disabled={nextLoading || uploadingIds.size > 0}
        >
          {nextLoading ? (
            "Saving…"
          ) : (
            <>
              Next: Lien Waivers <NextIcon />
            </>
          )}
        </Button>
      </div>
    </section>
  );
};

export default LineItemsStep;
